/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { 
  Bell, 
  AlertTriangle, 
  Calendar, 
  Headset, 
  ExternalLink, 
  X 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface RenewalWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  fechaVencimiento: string;
  diasRestantes: number;
  nombreEmpresa?: string;
}

export default function RenewalWarningModal({
  isOpen,
  onClose,
  fechaVencimiento,
  diasRestantes,
  nombreEmpresa
}: RenewalWarningModalProps) {
  if (!isOpen) return null;

  // Format date in Spanish (e.g. "28 de julio de 2026")
  let formattedDate = 'Fecha no definida';
  try {
    const d = new Date(fechaVencimiento);
    if (!isNaN(d.getTime())) {
      formattedDate = d.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  } catch (e) {
    console.error('Error formatting renewal expiration date:', e);
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto font-sans" id="renewal-warning-modal">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal Wrapper */}
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg transform overflow-hidden rounded-3xl bg-white p-6 sm:p-8 text-center shadow-2xl border border-slate-150 transition-all space-y-5"
          >
            {/* Close Icon Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
              title="Cerrar aviso"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Centered Yellow Bell Header Icon */}
            <div className="relative mx-auto w-20 h-20 bg-amber-100/80 rounded-full flex items-center justify-center border border-amber-200/80">
              <Bell className="h-10 w-10 text-amber-500 fill-amber-300/40" />
              <div className="absolute top-1 right-1 bg-amber-500 text-white rounded-full p-1 border-2 border-white shadow-xs">
                <AlertTriangle className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* Main Headline Title */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-center gap-2 text-slate-900 font-extrabold text-xl sm:text-2xl tracking-tight">
                <AlertTriangle className="h-6 w-6 text-amber-500 fill-amber-100 shrink-0" />
                <span>Recordatorio de Renovación</span>
              </div>
              <p className="text-sm sm:text-base font-bold text-slate-800">
                Su licencia de acceso al aplicativo vence en{' '}
                <span className="text-amber-600 font-black text-base sm:text-lg">
                  {diasRestantes <= 0 ? 'hoy' : `${diasRestantes} días`}.
                </span>
              </p>
            </div>

            {/* Paragraph Description */}
            <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed max-w-md mx-auto">
              Para garantizar el acceso continuo a sus proyectos, información y funcionalidades, le invitamos a renovar su suscripción antes de la fecha de vencimiento.
            </p>

            {/* Yellow Box for Expiration Date */}
            <div className="bg-amber-50/80 border border-amber-200/90 rounded-2xl p-4 flex items-center justify-center gap-4 shadow-2xs my-2">
              <div className="p-2.5 bg-amber-100/90 text-amber-600 rounded-xl shrink-0">
                <Calendar className="h-7 w-7" />
              </div>
              <div className="text-left">
                <span className="block text-xs font-black uppercase text-slate-900 tracking-wider">
                  Fecha de vencimiento:
                </span>
                <span className="block text-lg sm:text-xl font-black text-amber-600 tracking-tight capitalize mt-0.5">
                  {formattedDate}
                </span>
              </div>
            </div>

            {/* Headset Support Notice */}
            <div className="flex items-start gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100 text-left">
              <div className="p-2 bg-slate-900 text-amber-400 rounded-full shrink-0 mt-0.5 flex items-center justify-center h-8 w-8">
                <Headset className="h-4 w-4" />
              </div>
              <div className="text-xs text-slate-600 font-medium leading-relaxed">
                <p>
                  Si tiene alguna inquietud o requiere apoyo con el proceso de renovación, nuestro equipo estará disponible para atenderle en el <strong className="text-slate-900">+57 323 710 0983</strong>.
                </p>
                <p className="font-extrabold text-slate-900 mt-1">
                  ¡Gracias por elegir nuestra plataforma!
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://wa.me/message/LJIJE2OKHR35D1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-7 py-3 bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md flex items-center justify-center gap-2"
                id="btn-renovar-ahora"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Renovar ahora</span>
              </a>

              <button
                onClick={onClose}
                className="w-full sm:w-auto px-5 py-3 text-indigo-600 hover:text-indigo-800 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                id="btn-mas-tarde"
              >
                Más tarde
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
